import { cn } from "@/utils/cn";
import { IconSelector, IconX } from "@tabler/icons-react";
import clsx from "clsx";
import ReactSelect, {
	components,
	type ClearIndicatorProps,
	type DropdownIndicatorProps,
	type OptionProps,
	type MenuPlacement,
	type SingleValue,
} from "react-select";

type SelectOption = {
	value?: string | number | null;
	label: string;
};

type SelectProps = {
	placeholder?: string;
	size?: "default" | "sm" | "md" | "lg";
	portal?: boolean;
	className?: string;
	clearable?: boolean;
	disabled?: boolean;
	options: SelectOption[];
	minHeight?: number;
	maxHeight?: number;
	onValueChange?: (value: SelectOption["value"]) => void;
	defaultValue?: SelectOption["value"];
	isUnstyled?: boolean;
	menuPlacement?: "auto" | "bottom" | "top";
};

const ClearIndicator = (props: ClearIndicatorProps<SelectOption, false>) => (
	<components.ClearIndicator {...props}>
		<IconX size={18} stroke={1.5} />
	</components.ClearIndicator>
);

const DropdownIndicator = (props: DropdownIndicatorProps<SelectOption, false>) => (
	<components.DropdownIndicator {...props}>
		<IconSelector size={18} stroke={1.5} />
	</components.DropdownIndicator>
);

const Option = (props: OptionProps<SelectOption, false>) => <components.Option {...props} />;

export default function Select({
	placeholder,
	clearable = true,
	size = "default",
	minHeight = 300,
	maxHeight = 400,
	portal = true,
	menuPlacement,
	...props
}: SelectProps) {
	const defaultValue = props.options.find((option) => option.value === props.defaultValue);

	const selectOptions = {
		defaultOptions: true,
		defaultValue,
		options: props.options,
		isClearable: clearable,
		isDisabled: props.disabled,
		dropdownIndicator: "yes",
		minMenuHeight: minHeight,
		maxMenuHeight: maxHeight,
		menuPlacement: menuPlacement ?? ("auto" as MenuPlacement),
		onChange: (option: SingleValue<SelectOption>) => {
			if (props.onValueChange) {
				props.onValueChange(option?.value || null);
			}
		},
	};

	return (
		<ReactSelect
			key={props.defaultValue}
			components={{ ClearIndicator, DropdownIndicator, Option }}
			placeholder={placeholder}
			classNames={
				props.isUnstyled
					? {
							control: ({ isDisabled }) =>
								cn(
									"flex flex-row w-full  pl-3 !pr-0 gap-3 ",
									size === "default" && "!min-h-[38px] max-h-[38px] h-[38px] text-base !rounded-md",
									size === "sm" && "!min-h-[28px] max-h-[28px] h-[28px] px-2 text-sm rounded-md",
									size === "md" && "!min-h-[38px] max-h-[38px] h-[38px] text-base !rounded-md",
									size === "lg" && "!min-h-[48px] max-h-[48px] h-[48px] px-2 text-lg  rounded-lg",
									isDisabled
										? "!bg-muted !border-0 !text-muted-foreground !cursor-not-allowed"
										: "!bg-background !border-transparent focus-within:!border-focus-border focus-within:!ring-0 focus-within:!ring-focus-ring/30 !ring-offset-0 !outline-0",
								),
							valueContainer: () => "!p-0",
							placeholder: () => "!text-muted-foreground",
							singleValue: ({ isDisabled }) => clsx(isDisabled ? "!text-muted-foreground" : "!text-foreground"),
							menu: () => "!mt-1 !bg-background !border-1 !border-border !rounded-md !z-[90]",
							option: ({ isFocused, isSelected }) =>
								cn(
									"!text-foreground !cursor-pointer",
									size === "default" && "!px-3 !py-2 !text-base",
									size === "sm" && "!px-2.5 !py-1.5 !text-sm",
									size === "md" && "!px-3 !py-2 !text-base",
									size === "lg" && "!px-3 !py-2 !text-lg",
									isSelected ? "!bg-blue-100 !text-foreground" : isFocused ? "!bg-blue-50" : "!text-foreground",
								),
							multiValue: () => "inline-flex items-center !h-[24px] !max-h-[24px] !p-1 !rounded-md !bg-primary-100 ",
							multiValueLabel: () => "!text-sm !text-primary-600",
							multiValueRemove: () => "!text-sm !text-muted-foreground hover:!text-foreground !bg-transparent cursor-pointer",
							dropdownIndicator: () => "inline-flex items-center h-full !py-0 !text-muted-foreground",
							clearIndicator: () => "inline-flex items-center h-full !py-0 !text-muted-foreground !cursor-pointer",
						}
					: {
							control: ({ isDisabled }) =>
								cn(
									"flex flex-row w-full  pl-3 !pr-0 gap-3 ",
									size === "default" && "!min-h-[38px] max-h-[38px] h-[38px] text-base !rounded-md",
									size === "sm" && "!min-h-[28px] max-h-[28px] h-[28px] px-2 text-sm rounded-md",
									size === "md" && "!min-h-[38px] max-h-[38px] h-[38px] text-base !rounded-md",
									size === "lg" && "!min-h-[48px] max-h-[48px] h-[48px] px-2 text-lg  rounded-lg",
									isDisabled
										? "!bg-muted !border-border !text-muted-foreground !cursor-not-allowed"
										: "!bg-background !border-border focus-within:!border-focus-border focus-within:!ring-2 focus-within:!ring-focus-ring/30 !ring-offset-0 !outline-0",
								),
							valueContainer: () => "!p-0",
							placeholder: () => "!text-muted-foreground",
							singleValue: ({ isDisabled }) => clsx(isDisabled ? "!text-muted-foreground" : "!text-foreground"),
							menu: () => "!mt-1 !bg-background !border-1 !border-border !rounded-md !z-[90]",
							option: ({ isFocused, isSelected }) =>
								cn(
									"!text-foreground !cursor-pointer",
									size === "default" && "!px-3 !py-2 !text-base",
									size === "sm" && "!px-2.5 !py-1.5 !text-sm",
									size === "md" && "!px-3 !py-2 !text-base",
									size === "lg" && "!px-3 !py-2 !text-lg",
									isSelected ? "!bg-blue-100 !text-foreground" : isFocused ? "!bg-blue-50" : "!text-foreground",
								),
							multiValue: () => "inline-flex items-center !h-[24px] !max-h-[24px] !p-1 !rounded-md !bg-primary-100 ",
							multiValueLabel: () => "!text-sm !text-primary-600",
							multiValueRemove: () => "!text-sm !text-muted-foreground hover:!text-foreground !bg-transparent cursor-pointer",
							dropdownIndicator: () => "inline-flex items-center h-full !py-0 !text-muted-foreground",
							clearIndicator: () => "inline-flex items-center h-full !py-0 !text-muted-foreground !cursor-pointer",
						}
			}
			className={clsx("w-full text-base", props.className)}
			{...selectOptions}
		/>
	);
}
